import { z } from 'zod';

export const moduleSelectionSchema = z.object({
  moduleKey: z.string().min(1, 'Module key is required'),
  sourceId: z.string().min(1, 'Source ID is required'),
});

export type ModuleSelectionFormData = z.infer<typeof moduleSelectionSchema>;

export const modulesArraySchema = z
  .array(moduleSelectionSchema)
  .min(1, 'At least one module must be selected');

export type ModulesArrayFormData = z.infer<typeof modulesArraySchema>;

// Source ID format patterns for different modules
export const SOURCE_ID_PATTERNS: Record<string, { pattern: RegExp; hint: string; example: string }> = {
  Giving: {
    pattern: /^\d{4,10}$/,
    hint: 'Numeric ID (4-10 digits)',
    example: '12345',
  },
  SimpleChurch: {
    pattern: /^[a-zA-Z0-9_-]{4,50}$/,
    hint: 'Alphanumeric ID (4-50 characters)',
    example: 'church-id-123',
  },
  ServicePlanning: {
    pattern: /^[a-zA-Z0-9_-]{4,50}$/,
    hint: 'Alphanumeric ID (4-50 characters)',
    example: 'service-123',
  },
};

export function validateSourceId(moduleKey: string, sourceId: string): { valid: boolean; error?: string } {
  const config = SOURCE_ID_PATTERNS[moduleKey];

  if (!config) {
    // Unknown module - accept any non-empty string
    return sourceId.trim().length > 0
      ? { valid: true }
      : { valid: false, error: 'Source ID is required' };
  }

  if (!sourceId.trim()) {
    return { valid: false, error: 'Source ID is required' };
  }

  if (!config.pattern.test(sourceId)) {
    return { valid: false, error: `Invalid format. ${config.hint}. Example: ${config.example}` };
  }

  return { valid: true };
}
